import { PropsWithChildren } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '@givebutter/primitives/toast';
import { faCopy, faEye, faLink, faTimes, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as CancelPledge from 'assets/js/pages/pledges/CancelPledgeDialog';
import * as DeletePledge from 'assets/js/pages/pledges/DeletePledgeDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import { useCopy } from 'components/snippet';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { Pledge } from 'types/domain';

interface PledgeActionsRootProps extends PropsWithChildren {
    pledge: Omit<Pledge, 'installments'>;
}

const PledgeActionsRoot = ({ pledge, children }: PledgeActionsRootProps) => {
    const { pledge_id } = useParams<{
        pledge_id: string;
    }>();
    const { accountId } = useCurrentAccount();
    const { onClickCopy } = useCopy();
    const toast = useToast();
    const canView = !pledge_id;

    return (
        <DropdownMenu.Root>
            {children}
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    onSelect={e => e.preventDefault()}
                    sideOffset={5}
                    align={'end'}
                >
                    {canView && (
                        <DropdownMenu.LinkItem
                            to={`/accounts/${accountId}/pledges/${pledge.id}/edit`}
                        >
                            <p className="flex w-full items-center gap-2">
                                <FontAwesomeIcon className="size-4" icon={faEye} />
                                View details
                            </p>
                        </DropdownMenu.LinkItem>
                    )}
                    {pledge.can_cancel && (
                        <CancelPledge.Root itemId={pledge.id}>
                            <CancelPledge.Trigger asChild>
                                <DropdownMenu.Item
                                    onSelect={e => e.preventDefault()}
                                    className="flex items-center gap-2"
                                >
                                    <FontAwesomeIcon className="size-4" icon={faTimes} />
                                    Cancel pledge
                                </DropdownMenu.Item>
                            </CancelPledge.Trigger>
                        </CancelPledge.Root>
                    )}
                    <DropdownMenu.LinkItem
                        to={`/accounts/${accountId}/pledges/${pledge.id}/duplicate`}
                    >
                        <p className="flex items-center gap-2">
                            <FontAwesomeIcon className="size-4" icon={faCopy} /> Duplicate pledge
                        </p>
                    </DropdownMenu.LinkItem>
                    <DropdownMenu.Item
                        onClick={e => {
                            onClickCopy(e, pledge.pay_url);
                            toast.success('Link copied');
                        }}
                        className="flex items-center gap-2"
                    >
                        <FontAwesomeIcon className="size-4" icon={faLink} />
                        Copy payment link
                    </DropdownMenu.Item>
                    {pledge.can_delete && (
                        <DeletePledge.Root itemId={pledge.id}>
                            <DeletePledge.Trigger asChild>
                                <DropdownMenu.Item
                                    className="flex items-center gap-2 text-red-500"
                                    onSelect={e => e.preventDefault()}
                                >
                                    <FontAwesomeIcon className="size-4" icon={faTrash} />
                                    Delete pledge
                                </DropdownMenu.Item>
                            </DeletePledge.Trigger>
                        </DeletePledge.Root>
                    )}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

const Root = PledgeActionsRoot;
const PledgeActionsTrigger = DropdownMenu.Trigger;
const Trigger = PledgeActionsTrigger;

export {
    PledgeActionsRoot,
    PledgeActionsTrigger,
    //
    Root,
    Trigger,
};
